import { OCRSetupModal } from 'pages/Transactions/TransactionViewPage/MessageViewPage/MessageHeader/ActionButtons/actions/components/OCRSetupModal/OCRSetupModal';
import { DocumentTypeWithVirtual, MessageV2DTO, TransactionState } from 'support/types';
import { useTranslation } from 'react-i18next';
import { useOCRAvailability } from 'support/helpers/ocr/ocr';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { DocumentType } from 'support/types';
import { routeToCreatePage } from 'support/helpers/navigation/navigation';
import { ActionConfig } from '../components/types';
import { AlreadyExistingMessageModal } from './components/AlreadyExistingMessageModal/AlreadyExistingMessageModal';
import { mapVirtualToActualDocumentType } from 'services/documentTypes/documentTypes';

// Custom hook for creating document creation actions with OCR support
export const useDocumentActionWithOCR = (
  message: MessageV2DTO,
  documentType: DocumentType,
  relatedTransactions: Array<MessageV2DTO> | undefined,
  ocrAvailability: ReturnType<typeof useOCRAvailability>,
  baseConfig: Omit<ActionConfig, 'onClick' | 'documentTypeExists'>,
): ActionConfig => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSetupOCRModalOpen, setIsSetupOCRModalOpen] = useState(false);
  const [showExistingDocumentModal, setShowExistingDocumentModal] = useState(false);

  const documentTypeExists = relatedTransactions?.some(
    (transaction) =>
      mapVirtualToActualDocumentType(transaction.type as DocumentTypeWithVirtual) === documentType &&
      [TransactionState.success, TransactionState.pending].includes(transaction.state as TransactionState),
  );

  const navigateToForm = (showUploadModal?: boolean) => {
    navigate(routeToCreatePage(message.id, documentType, showUploadModal), { state: { keepBreadcrumbs: true } });
  };

  const mainActionOnClick = () => {
    if (documentTypeExists) {
      setShowExistingDocumentModal(true);
    } else {
      navigateToForm();
    }
  };

  const openSetupOCRModal = () => {
    setIsSetupOCRModalOpen(true);
  };

  const hideModal = () => {
    setShowExistingDocumentModal(false);
  };

  const confirmCreateAgain = () => {
    hideModal();
    navigateToForm();
  };

  const baseAction: ActionConfig = {
    ...baseConfig,
    label: baseConfig.label,
    documentType,
    analyticsId: baseConfig.analyticsId,
    onClick: mainActionOnClick,
    test: (message) => {
      const isDiscarded = message.state === TransactionState.dropped;
      if (isDiscarded) {
        return false;
      }
      if (baseConfig.test) {
        return baseConfig.test(message);
      }

      return true;
    },
    documentTypeExists,
    renderModal: () => (
      <>
        <OCRSetupModal
          setOpen={setIsSetupOCRModalOpen}
          open={isSetupOCRModalOpen}
          sourceMessageId={message.id}
          documentType={documentType}
        />
        <AlreadyExistingMessageModal
          isOpen={showExistingDocumentModal}
          onAction={confirmCreateAgain}
          onClose={hideModal}
        />
      </>
    ),
  };

  if (!ocrAvailability.shouldShowOCR) {
    return baseAction;
  }

  const manualAction: ActionConfig = {
    label: t('transactions:show.actionButtons.createManually'),
    onClick: () => navigateToForm(),
    analyticsId: 'transactions:create_manually',
    analyticsProperties: {
      documentType,
    },
  };

  const uploadAction: ActionConfig = {
    label: t('transactions:show.actionButtons.uploadDocument.label'),
    onClick: ocrAvailability.isBeta ? openSetupOCRModal : () => navigateToForm(true),
    analyticsId: 'transactions:upload_document',
    analyticsProperties: {
      documentType,
    },
    badge:
      ocrAvailability.isBeta && !ocrAvailability.isLoading
        ? t('transactions:show.actionButtons.uploadDocument.badge')
        : undefined,
    description:
      ocrAvailability.isBeta && !ocrAvailability.isLoading
        ? t('transactions:show.actionButtons.uploadDocument.description', {
            documentType: t(`common:messageTypes.plural.${documentType}`),
          })
        : undefined,
  };

  return {
    ...baseAction,
    subActions: baseAction.subActions ?? [manualAction, uploadAction],
  };
};

export const useContainsSearchQueryAction = (action: string) => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('action');
  return searchQuery === action;
};
